/**
 * ChipInput — a token/chip editor for a capped list of short strings (automation preferences:
 * Daily Brief news topics, Email focus people, Overnight pinned topics).
 *
 * Type a value and press Enter or comma to commit it as a removable chip; pasting (or typing) a
 * comma/newline-separated list splits into multiple chips; each chip has an ✕ to remove it;
 * Backspace on an empty input removes the last chip; blurring commits whatever is typed (so a value
 * left in the box is captured on Save). Values are trimmed, de-duped case-insensitively, and capped
 * at `max`. The chip row scrolls (xv-scroll) once it overflows. Fully controlled — the parent owns
 * the array; only the in-progress draft lives in local state.
 *
 * OPTIONAL typeahead (Focus people): pass `suggestions` + `onQueryChange` to get a debounced
 * dropdown of existing contacts the parent resolves (see useContactSuggestions). ChipInput stays
 * PRESENTATIONAL — it owns no network/session; the parent does the lookup and hands back the list.
 * When neither prop is passed the component behaves byte-identically to the plain free-text chip
 * editor (the two topic inputs).
 */
import {
  useEffect,
  useState,
  type KeyboardEvent,
  type ChangeEvent,
} from "react";

/** One typeahead row. `value` is what gets committed as a chip (folded/deduped like raw text). */
export interface ChipSuggestion {
  label: string;
  sublabel?: string;
  value: string;
}

export function ChipInput({
  id,
  label,
  value,
  max,
  placeholder,
  onChange,
  suggestions,
  onQueryChange,
}: {
  id: string;
  label: string;
  value: string[] | undefined;
  max: number;
  placeholder: string;
  onChange: (next: string[]) => void;
  /** Optional contact/typeahead rows; when present (and non-empty with a non-empty draft) a dropdown
   *  renders. Omit for the plain free-text inputs. */
  suggestions?: ChipSuggestion[];
  /** Optional: called with the current draft text on every change so the parent can run a lookup. */
  onQueryChange?: (query: string) => void;
}) {
  const chips = value ?? [];
  const [draft, setDraft] = useState("");
  // Typeahead-only state (inert when no suggestion props are passed).
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const atMax = chips.length >= max;

  const list = suggestions ?? [];
  const showDropdown = !dismissed && list.length > 0 && draft.length > 0;

  // Reset the highlight whenever the result set changes, so a stale index never points past the
  // new list (or at a different row than the user expects).
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions]);

  /** Fold raw segments onto the current chips (trim, cap, case-insensitive de-dupe) in ONE pass —
   *  reading `chips` once avoids the stale-closure multi-add bug. Returns the resulting array. */
  const fold = (segments: string[]): string[] => {
    let next = chips;
    for (const seg of segments) {
      const v = seg.trim();
      if (!v || next.length >= max) continue;
      if (next.some((c) => c.toLowerCase() === v.toLowerCase())) continue;
      next = [...next, v];
    }
    return next;
  };

  const commit = (raw: string) => {
    // At max we keep the draft so the text + "max reached" hint stay visible rather than vanishing.
    if (atMax) return;
    const next = fold([raw]);
    setDraft("");
    setSelectedIndex(-1);
    setDismissed(false);
    onQueryChange?.(""); // clear the parent lookup so the dropdown empties after a commit
    if (next !== chips) onChange(next);
  };

  /** Update the draft and (when wired) notify the parent so it can refresh suggestions. */
  const updateDraft = (next: string) => {
    setDraft(next);
    setSelectedIndex(-1);
    setDismissed(false);
    onQueryChange?.(next);
  };

  const onInputChange = (raw: string) => {
    if (!/[,\n]/.test(raw)) {
      updateDraft(raw);
      return;
    }
    // A value carrying separators (a paste or fast typing): commit the complete segments, keep the
    // trailing unterminated one editable in the box.
    const parts = raw.split(/[,\n]/);
    const tail = parts.pop() ?? "";
    const next = fold(parts);
    if (next !== chips) onChange(next);
    updateDraft(tail.trimStart()); // drop the space after the separator; keep what's still being typed
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showDropdown && e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1 >= list.length ? 0 : i + 1));
      return;
    }
    if (showDropdown && e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 < 0 ? list.length - 1 : i - 1));
      return;
    }
    if (showDropdown && e.key === "Escape") {
      e.preventDefault();
      setDismissed(true);
      setSelectedIndex(-1);
      return;
    }
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      // A highlighted suggestion wins; otherwise fall back to committing the raw draft.
      if (showDropdown && selectedIndex >= 0 && list[selectedIndex]) {
        commit(list[selectedIndex].value);
      } else {
        commit(draft);
      }
    } else if (e.key === "Backspace" && draft === "" && chips.length > 0) {
      e.preventDefault();
      onChange(chips.slice(0, -1));
    }
  };

  return (
    <div className="prefs__field">
      <label className="prefs__label" htmlFor={id}>
        {label}
      </label>
      <div className="prefs__chips-wrap">
        <div className="prefs__chips xv-scroll">
          {chips.map((chip, i) => (
            <span key={`${chip}-${i}`} className="prefs__chip">
              <span className="prefs__chip-label" title={chip}>
                {chip}
              </span>
              <button
                type="button"
                className="prefs__chip-x"
                aria-label={`Remove ${chip}`}
                // Don't steal focus / blur the input — otherwise removing a chip while text is typed
                // would blur-commit that draft as a surprise chip.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChange(chips.filter((_, idx) => idx !== i))}
              >
                ✕
              </button>
            </span>
          ))}
          <input
            id={id}
            className="prefs__chip-input"
            value={draft}
            placeholder={chips.length === 0 ? placeholder : ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onInputChange(e.target.value)
            }
            onKeyDown={onKeyDown}
            onBlur={() => commit(draft)}
          />
        </div>
        {showDropdown && (
          <div className="prefs__chip-suggestions xv-scroll" role="listbox">
            {list.map((s, i) => (
              <button
                key={`${s.value}-${i}`}
                type="button"
                role="option"
                aria-selected={i === selectedIndex}
                className={`prefs__chip-suggestion${i === selectedIndex ? " is-selected" : ""}`}
                // mousedown (not click) + preventDefault so selecting a row doesn't blur the input
                // first — a blur would commit the raw draft before the suggestion lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(s.value);
                }}
              >
                <span className="prefs__chip-suggestion-label">{s.label}</span>
                {s.sublabel ? (
                  <span className="prefs__chip-suggestion-sub">
                    {s.sublabel}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="prefs__hint">
        {atMax
          ? `Max ${max} reached.`
          : `Up to ${max} — press Enter or comma to add.`}
      </p>
    </div>
  );
}
